import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { rgba } from 'emotion-rgba'
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  memo,
} from 'react'

export interface Props {
  className?: string
  disabled?: boolean
  id?: string
  name: string
  type?: string
  placeholder?: string
  error?: boolean
  isDirty?: boolean
  required?: boolean
  variant?: Variant
  value?: string
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
}

export const Input = memo(
  forwardRef<HTMLInputElement, Props>(function Input(
    {
      className,
      disabled,
      id,
      name,
      type,
      placeholder,
      error = false,
      isDirty = false,
      required,
      variant = 'text',
      value,
      onBlur,
      onChange,
      onFocus,
    },
    ref,
  ) {
    return (
      <Container
        ref={ref}
        className={className}
        error={error}
        id={id}
        isDirty={isDirty}
        disabled={disabled}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        variant={variant}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    )
  }),
)

Input.displayName = 'Input'

const Container = styled.input<ContainerProps>`
  ${({ theme, error, variant }) => {
    return css`
      :-webkit-autofill,
      :-webkit-autofill:hover,
      :-webkit-autofill:focus,
      :-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 100px ${theme.colors.variants.primaryDark}
          inset;
        -webkit-text-fill-color: ${theme.colors.variants.neutralLight3};
      }

      ${variant === 'submit'
        ? css`
            display: inline-block;
            height: 3rem;
            background: ${theme.colors.variants.neutralDark2};
            color: ${theme.colors.variants.neutralLight4};
            font-family: ${theme.fontFamily.paragraph};
            font-size: 0.9375rem;
            font-weight: 500;
            margin: 2.75rem auto 0;
            padding: 0 1.875rem;
            text-align: center;
            transition: 0.3s ease-in-out;
            z-index: 2;
            &:hover {
              background: ${theme.colors.variants.secondaryLight};
            }
            &:disabled {
              background: ${theme.colors.variants.neutralDark4};
              cursor: not-allowed;
            }
          `
        : css`
            width: 100%;
            height: 3.75rem;
            font-family: ${theme.fontFamily.paragraph};
            font-size: 0.9375rem;
            font-weight: 500;
            color: ${theme.colors.variants.neutralDark4};
            border-bottom: 0.125rem solid
              ${error
                ? theme.colors.variants.dangerLight
                : rgba(theme.colors.variants.neutralDark2, 0.3)};

            &::-webkit-input-placeholder {
              color: ${theme.colors.variants.neutralDark2};
              opacity: 1;
            }
            &::-moz-placeholder {
              color: ${theme.colors.variants.neutralDark2};
              opacity: 1;
            }
            &::-ms-input-placeholder {
              color: ${theme.colors.variants.neutralDark2};
              opacity: 1;
            }
            &::placeholder {
              color: ${theme.colors.variants.neutralDark2};
              opacity: 1;
            }
          `}
    `
  }}
`

interface ContainerProps {
  error: boolean
  isDirty: boolean
  variant: Variant
}

export type Variant = 'text' | 'checkbox' | 'submit'
